import {
  Badge,
  Button,
  TextField,
  ActionBar,
  ErrorBanner,
} from "frontend-kit/components/index.js";
import { getChainApp } from "./chain-app.js";
import {
  luxToDusk,
  duskToLux,
  isContractOwner,
} from "./chain-config.js";
import { loadTradingKey } from "./trading-key-store.js";
import {
  OP,
  encodeDisputeYesPayload,
  signTraderAuth,
  buildTraderAuthArg,
  hexToBytes,
} from "./trader-crypto.js";
import { showGlobalTx, trackTx } from "./tx-status.js";
import { withNonceRetry } from "./trader-nonce.js";
import { isAutoRetryEnabled } from "./trade-settings.js";
import { renderCouncilHandoff } from "./council-handoff.js";

function badgeVariant(status) {
  if (status === "under_review") return "danger";
  if (status === "finalized") return "success";
  if (status === "cancelled") return "neutral";
  return "pending";
}

/**
 * Resolution court snapshot + owner/trader actions (resolution v2).
 *
 * @param {{
 *   marketId: number|string,
 *   resolution: object|null,
 *   status: string,
 *   info?: object|null,
 *   wallet?: object|null,
 *   log?: (msg: string) => void,
 *   onRefresh?: () => void,
 * }} opts
 */
export function renderResolutionPanel({
  marketId,
  resolution,
  status,
  info,
  wallet,
  log,
  onRefresh,
}) {
  const root = document.createElement("div");
  root.className = "info-card resolution-card dt-card";

  // Status + resolution field detail (window/bond/dispute/winner) live in
  // the phase detail panel above the fold now (market-detail.js's
  // lifecycle bar, click a phase to see it) - this card is action-only:
  // assert/dispute/challenge/resolve/finalize/cancel, plus their bond +
  // evidence inputs and feedback.
  const head = document.createElement("div");
  head.className = "info-card-head";
  const title = document.createElement("strong");
  title.textContent = "Resolution actions";
  head.append(title);
  if (status) {
    head.append(Badge({ variant: badgeVariant(status), label: status }));
  }
  root.append(head);

  const feedback = document.createElement("div");
  feedback.className = "resolution-feedback";
  root.append(feedback);

  if (status === "under_review") {
    root.append(
      renderCouncilHandoff({
        marketId,
        pendingOutcome:
          resolution?.pending_outcome ?? resolution?.pendingOutcome ?? null,
      })
    );
  }

  const actionsHost = document.createElement("div");
  root.append(actionsHost);

  const owner = isContractOwner(wallet);
  const minBondLux = Number(info?.min_bond ?? 0);
  const minBondDusk = luxToDusk(minBondLux) || 0.1;

  let busy = false;
  let bondField = null;
  let evidenceField = null;

  if (status === "pending_yes" || status === "pending_no" || status === "under_review") {
    bondField = TextField({
      id: `pm-res-bond-${marketId}`,
      label: "Bond (DUSK)",
      value: String(minBondDusk),
      hint: `Minimum ${minBondDusk} DUSK`,
    });
    evidenceField = TextField({
      id: `pm-res-evidence-${marketId}`,
      label: "Evidence hash (optional hex)",
      value: "",
      hint: "64 hex chars; empty → zeros. Docs stay off-chain.",
    });
    root.insertBefore(bondField, actionsHost);
    root.insertBefore(evidenceField, actionsHost);
  }

  function fieldValue(el) {
    if (!el) return "";
    const input = el.querySelector("input, select, textarea");
    return input?.value?.trim() ?? "";
  }

  function showError(msg) {
    feedback.replaceChildren(ErrorBanner({ message: msg }));
  }

  function parseEvidence() {
    const raw = fieldValue(evidenceField).replace(/^0x/i, "");
    if (!raw) return new Uint8Array(32);
    if (!/^[0-9a-fA-F]{64}$/.test(raw)) {
      throw new Error("Evidence hash must be 64 hex characters (or empty).");
    }
    return hexToBytes(raw);
  }

  async function runTx(label, writeFn) {
    if (busy) return;
    busy = true;
    renderActions();
    feedback.replaceChildren();
    let ui;
    try {
      ui = showGlobalTx({ label });
      const dusk = await getChainApp();
      const tx = await writeFn(dusk);
      const tracked = await trackTx(tx, { ui, autoDismissMs: 10_000 });
      log?.(`${label} tx ${tracked.hash}`);
      onRefresh?.();
    } catch (e) {
      const msg = e.message || String(e);
      showError(msg);
      log?.(`${label} failed: ${msg}`);
      ui?.setStatus("failed", msg);
    } finally {
      busy = false;
      renderActions();
    }
  }

  function renderActions() {
    const buttons = [];

    if (owner && status === "open") {
      buttons.push(
        Button({
          label: busy ? "…" : "Assert YES",
          variant: "primary",
          disabled: busy,
          onClick: () =>
            runTx("Assert YES", (dusk) =>
              dusk.writeContract({
                contract: "predictionMarket",
                functionName: "assert_yes",
                args: Number(marketId),
                privacy: "public",
              })
            ),
        })
      );
    }

    if (status === "pending_yes") {
      buttons.push(
        Button({
          label: busy ? "…" : "Dispute YES",
          variant: "danger",
          disabled: busy,
          onClick: async () => {
            const bondDusk = Number(fieldValue(bondField));
            if (!Number.isFinite(bondDusk) || bondDusk <= 0) {
              showError("Bond must be a positive DUSK amount.");
              return;
            }
            const bond = Number(duskToLux(bondDusk));
            const evidence = parseEvidence();
            const key = loadTradingKey();
            if (!key?.sk_hex || !key?.pk_base58 || !key?.commitment_hex) {
              showError("Connect a trading key in Account settings");
              return;
            }
            // Not routed through the shared runTx() helper (unlike the
            // other buttons here) - runTx swallows its own errors, but
            // this needs to observe a nonce-rejection to retry, so it
            // manages busy/ui/log itself, matching trade-ticket.js's and
            // deposit-panel.js's pattern.
            if (busy) return;
            busy = true;
            renderActions();
            feedback.replaceChildren();
            let ui;
            try {
              ui = showGlobalTx({ label: "Dispute YES" });
              const dusk = await getChainApp();
              const commitment = Array.from(hexToBytes(key.commitment_hex));
              const readChainNonce = async () =>
                Number(
                  await dusk.readContract({
                    contract: "predictionMarket",
                    functionName: "next_nonce",
                    args: commitment,
                  })
                );
              const payload = encodeDisputeYesPayload(Number(marketId), bond, evidence);

              async function attemptDispute(nonce) {
                const { signatureHex } = await signTraderAuth({
                  skHex: key.sk_hex,
                  op: OP.DISPUTE_YES,
                  commitmentHex: key.commitment_hex,
                  nonce,
                  payloadBytes: payload,
                });
                const auth = buildTraderAuthArg({
                  pkBase58: key.pk_base58,
                  nonce,
                  signatureHex,
                });
                const tx = await dusk.writeContract({
                  contract: "predictionMarket",
                  functionName: "dispute_yes",
                  args: {
                    auth,
                    market_id: Number(marketId),
                    bond,
                    evidence_hash: Array.from(evidence),
                  },
                  privacy: "public",
                  deposit: String(bond),
                });
                return trackTx(tx, { ui, autoDismissMs: 10_000 });
              }

              // Nonce reserved through the shared per-commitment queue -
              // see trader-nonce.js. Retry on a genuine cross-tab/device
              // stale-nonce race is opt-in via Settings (trade-settings.js).
              const tracked = await withNonceRetry(
                key.commitment_hex,
                readChainNonce,
                attemptDispute,
                {
                  retryOnStaleNonce: isAutoRetryEnabled(),
                  onRetrying: (e) => {
                    ui.setStatus(
                      "retrying",
                      "That nonce was stale (another action beat it to the chain) - retrying with a fresh one…"
                    );
                    log?.(`Dispute YES rejected on a stale nonce (${e.message}) - retrying once with a fresh one`);
                  },
                }
              );
              log?.(`Dispute YES tx ${tracked.hash}`);
              onRefresh?.();
            } catch (e) {
              const msg = e.message || String(e);
              showError(msg);
              log?.(`Dispute YES failed: ${msg}`);
              ui?.setStatus("failed", msg);
            } finally {
              busy = false;
              renderActions();
            }
          },
        })
      );
    }

    if (owner && status === "pending_no") {
      buttons.push(
        Button({
          label: busy ? "…" : "Challenge NO (late YES)",
          variant: "primary",
          disabled: busy,
          onClick: async () => {
            try {
              const bondDusk = Number(fieldValue(bondField));
              if (!Number.isFinite(bondDusk) || bondDusk <= 0) {
                showError("Bond must be a positive DUSK amount.");
                return;
              }
              const bond = Number(duskToLux(bondDusk));
              const evidence = parseEvidence();
              await runTx("Challenge NO", (dusk) =>
                dusk.writeContract({
                  contract: "predictionMarket",
                  functionName: "challenge_no",
                  args: {
                    market_id: Number(marketId),
                    bond,
                    evidence_hash: Array.from(evidence),
                  },
                  privacy: "public",
                  deposit: String(bond),
                })
              );
            } catch (e) {
              showError(e.message || String(e));
            }
          },
        })
      );
    }

    // Owner escape hatch only - council path is multisig-tool (see handoff above).
    if (owner && status === "under_review") {
      buttons.push(
        Button({
          label: busy ? "…" : "Owner escape: YES",
          variant: "primary",
          disabled: busy,
          onClick: () =>
            runTx("Resolve YES (owner)", (dusk) =>
              dusk.writeContract({
                contract: "predictionMarket",
                functionName: "resolve",
                args: {
                  market_id: Number(marketId),
                  winning_outcome: 0,
                  quorum_sigs: [],
                },
                privacy: "public",
              })
            ),
        }),
        Button({
          label: busy ? "…" : "Owner escape: NO",
          variant: "danger",
          disabled: busy,
          onClick: () =>
            runTx("Resolve NO (owner)", (dusk) =>
              dusk.writeContract({
                contract: "predictionMarket",
                functionName: "resolve",
                args: {
                  market_id: Number(marketId),
                  winning_outcome: 1,
                  quorum_sigs: [],
                },
                privacy: "public",
              })
            ),
        })
      );
    }

    if (
      status === "pending_yes" ||
      status === "pending_no" ||
      status === "under_review"
    ) {
      buttons.push(
        Button({
          label: busy ? "…" : "Finalize (if window ended)",
          disabled: busy,
          onClick: () =>
            runTx("Finalize", (dusk) =>
              dusk.writeContract({
                contract: "predictionMarket",
                functionName: "finalize_resolution",
                args: Number(marketId),
                privacy: "public",
              })
            ),
        })
      );
    }

    if (owner && status !== "finalized" && status !== "cancelled") {
      buttons.push(
        Button({
          label: busy ? "…" : "Cancel event",
          disabled: busy,
          onClick: () =>
            runTx("Cancel", (dusk) =>
              dusk.writeContract({
                contract: "predictionMarket",
                functionName: "cancel_market",
                args: Number(marketId),
                privacy: "public",
              })
            ),
        })
      );
    }

    if (owner && status === "finalized") {
      const winning =
        info?.winning_outcome ??
        resolution?.winning_outcome ??
        null;
      const losingReserve =
        winning === 0
          ? info?.no_reserve
          : winning === 1
            ? info?.yes_reserve
            : null;
      const lpFees = info?.lp_fee_pot;
      const canSweep =
        losingReserve != null && BigInt(losingReserve) > 0n;
      const canClaimFees =
        lpFees != null && BigInt(lpFees) > 0n;

      if (canSweep) {
        buttons.push(
          Button({
            label: busy
              ? "…"
              : `Sweep losing reserve (${luxToDusk(losingReserve)} DUSK)`,
            disabled: busy,
            onClick: () =>
              runTx("Sweep losing reserve", (dusk) =>
                dusk.writeContract({
                  contract: "predictionMarket",
                  functionName: "sweep_losing_reserve",
                  args: Number(marketId),
                  privacy: "public",
                })
              ),
          })
        );
      }
      if (canClaimFees) {
        buttons.push(
          Button({
            label: busy ? "…" : `Claim LP fees (${luxToDusk(lpFees)} DUSK)`,
            disabled: busy,
            onClick: () =>
              runTx("Claim LP fees", (dusk) =>
                dusk.writeContract({
                  contract: "predictionMarket",
                  functionName: "claim_lp_fees",
                  args: Number(marketId),
                  privacy: "public",
                })
              ),
          })
        );
      }
    }

    actionsHost.replaceChildren(buttons.length ? ActionBar(buttons) : document.createDocumentFragment());
  }

  renderActions();
  return root;
}
